import { useState } from "react";
import { ShieldCheck, BellRing, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { api, errMsg } from "@/lib/api";
import { SERVICES, IMG } from "@/lib/constants";
import { PageHead } from "@/pages/Services";
import { useSeo } from "@/lib/seo";

function MaidForce() {
  const { t } = useI18n();
  const [f, setF] = useState({ name: "", phone: "", email: "", city: "", service_area: "", years_experience: "", languages: "", availability: "", references: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.phone || !f.email || !f.city) { toast.error(t("req.required")); return; }
    setBusy(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, k === "years_experience" ? v || "0" : v));
    fd.append("kind", "maid");
    files.forEach((x) => fd.append("files", x));
    try { const r = await api.post("/applications", fd); setDone(r.data.ref); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <section id="maids" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 scroll-mt-24" data-testid="maid-force-section">
      <div className="rounded-3xl bg-gradient-to-br from-sky-500 via-blue-600 to-indigo-700 text-white p-8 md:p-10 grid lg:grid-cols-[1fr_1.6fr] gap-8">
        <div>
          <Sparkles className="h-8 w-8 text-amber-300" />
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">{t("mj.title")}</h2>
          <p className="mt-2 text-blue-50">{t("mj.copy")}</p>
        </div>
        {done ? (
          <div className="rounded-2xl bg-white text-slate-900 p-8 text-center" data-testid="maid-success"><CheckCircle2 className="h-10 w-10 text-blue-600 mx-auto" /><p className="mt-3 font-semibold">{t("join.success", { ref: done })}</p></div>
        ) : (
          <form onSubmit={submit} className="rounded-2xl bg-white text-slate-900 p-6 grid sm:grid-cols-2 gap-4" data-testid="maid-form">
            <F label={`${t("f.name")} *`}><Input value={f.name} onChange={set("name")} data-testid="maid-name-input" /></F>
            <F label={`${t("f.phone")} *`}><Input value={f.phone} onChange={set("phone")} data-testid="maid-phone-input" /></F>
            <F label={`${t("f.email")} *`}><Input type="email" value={f.email} onChange={set("email")} data-testid="maid-email-input" /></F>
            <F label={`${t("f.city")} *`}><Input value={f.city} onChange={set("city")} data-testid="maid-city-input" /></F>
            <F label={t("f.area")}><Input value={f.service_area} onChange={set("service_area")} data-testid="maid-area-input" /></F>
            <F label={t("f.years")}><Input type="number" min="0" value={f.years_experience} onChange={set("years_experience")} data-testid="maid-years-input" /></F>
            <F label={t("f.languages")}><Input value={f.languages} onChange={set("languages")} placeholder="Spanish, English" data-testid="maid-languages-input" /></F>
            <F label={t("mj.days")}><Input value={f.availability} onChange={set("availability")} data-testid="maid-availability-input" /></F>
            <div className="sm:col-span-2"><F label={t("f.refs")}><Textarea rows={2} value={f.references} onChange={set("references")} data-testid="maid-refs-input" /></F></div>
            <div className="sm:col-span-2"><F label={t("mj.files")}><Input type="file" multiple accept="image/*,application/pdf" onChange={(e) => setFiles([...e.target.files])} data-testid="maid-files-input" /></F></div>
            <Button type="submit" disabled={busy} className="sm:col-span-2 h-12 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold" data-testid="maid-submit-btn">
              {busy && <Loader2 className="h-4 w-4 me-2 animate-spin" />}{t("mj.submit")}
            </Button>
          </form>
        )}
      </div>
    </section>
  );
}

const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-sm text-slate-700">{label}</Label>{children}</div>;

export default function JoinPro() {
  const { t } = useI18n();
  useSeo(t("nav.join"), t("join.copy"));
  const [f, setF] = useState({ name: "", phone: "", email: "", city: "", service_area: "", years_experience: "", languages: "", availability: "", references: "" });
  const [services, setServices] = useState([]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (k) => setServices(services.includes(k) ? services.filter((x) => x !== k) : [...services, k]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.phone || !f.email || !f.city || !services.length) { toast.error(t("req.required")); return; }
    setBusy(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, k === "years_experience" ? v || "0" : v));
    fd.append("services", services.join(","));
    files.forEach((x) => fd.append("files", x));
    try { const r = await api.post("/applications", fd); setDone(r.data.ref); }
    catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  return (
    <div data-testid="join-page">
      <PageHead title={t("join.title")} intro={t("join.copy")} img={IMG.pro} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4 mb-6"><a href="#maids" className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-blue-700 px-4 py-2 text-sm font-semibold hover:bg-blue-100 transition-colors" data-testid="join-maid-link"><Sparkles className="h-4 w-4" />{t("mj.cta")}</a></div>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 grid lg:grid-cols-[1fr_1.6fr] gap-8">
        <div className="space-y-4">
          <div className="rounded-2xl bg-white border border-slate-200 p-6"><ShieldCheck className="h-6 w-6 text-teal-600" /><p className="mt-3 font-semibold">{t("join.checks_t")}</p><p className="text-sm text-slate-500 mt-1">{t("join.checks_d")}</p></div>
          <div className="rounded-2xl bg-slate-900 text-white p-6"><BellRing className="h-6 w-6 text-amber-400" /><p className="mt-3 font-semibold">{t("join.alerts_t")}</p><p className="text-sm text-slate-300 mt-1">{t("join.alerts_d")}</p></div>
        </div>
        {done ? (
          <div className="rounded-3xl bg-white border border-teal-200 p-10 text-center" data-testid="join-success">
            <CheckCircle2 className="h-12 w-12 text-teal-600 mx-auto" />
            <p className="mt-4 text-lg font-semibold">{t("join.success", { ref: done })}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-3xl bg-white border border-slate-200 p-6 md:p-8 grid sm:grid-cols-2 gap-5" data-testid="join-form">
            <F label={`${t("f.name")} *`}><Input value={f.name} onChange={set("name")} data-testid="join-name-input" /></F>
            <F label={`${t("f.phone")} *`}><Input value={f.phone} onChange={set("phone")} data-testid="join-phone-input" /></F>
            <F label={`${t("f.email")} *`}><Input type="email" value={f.email} onChange={set("email")} data-testid="join-email-input" /></F>
            <F label={`${t("f.city")} *`}><Input value={f.city} onChange={set("city")} data-testid="join-city-input" /></F>
            <div className="sm:col-span-2"><F label={t("f.area")}><Input value={f.service_area} onChange={set("service_area")} data-testid="join-area-input" /></F></div>
            <div className="sm:col-span-2">
              <Label className="text-sm text-slate-700">{t("f.services")} *</Label>
              <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {SERVICES.map(({ key }) => (
                  <label key={key} className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm cursor-pointer hover:border-teal-400">
                    <Checkbox checked={services.includes(key)} onCheckedChange={() => toggle(key)} data-testid={`join-service-${key}`} />{t(`svc.${key}`)}
                  </label>
                ))}
              </div>
            </div>
            <F label={t("f.years")}><Input type="number" min="0" value={f.years_experience} onChange={set("years_experience")} data-testid="join-years-input" /></F>
            <F label={t("f.languages")}><Input value={f.languages} onChange={set("languages")} placeholder="Spanish, English" data-testid="join-languages-input" /></F>
            <div className="sm:col-span-2"><F label={t("f.availability")}><Input value={f.availability} onChange={set("availability")} data-testid="join-availability-input" /></F></div>
            <div className="sm:col-span-2"><F label={t("f.refs")}><Textarea value={f.references} onChange={set("references")} data-testid="join-refs-input" /></F></div>
            <div className="sm:col-span-2"><F label={t("f.files")}><Input type="file" multiple accept="image/*,application/pdf" onChange={(e) => setFiles([...e.target.files])} data-testid="join-files-input" /></F></div>
            <Button type="submit" disabled={busy} className="sm:col-span-2 h-12 rounded-full bg-teal-600 hover:bg-teal-700 font-semibold" data-testid="join-submit-btn">
              {busy && <Loader2 className="h-4 w-4 me-2 animate-spin" />}{t("join.submit")}
            </Button>
          </form>
        )}
      </section>
      <MaidForce />
    </div>
  );
}
